import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { CoverImage, BrandLogo } from "@seek/branding-assets";
import { Disclosure, Text, Strong, Box, Stack, IconCritical, IconInfo, useResponsiveValue, useToast, Inline, Bleed, ButtonIcon, IconThumb, Notice, IconCopy, List, IconPositive, Spread, ButtonLink, IconChevron, Dialog, Heading, Hidden, Columns, Column } from "braid-design-system";
import React, { useState, useCallback, createContext, useMemo, useContext, useEffect, useRef, forwardRef } from "react";
import { useInView } from "react-intersection-observer";
import { useMelwaysInfo } from "@seek/melways-react";
import { useJobDetailsViewContext, useQualities } from "../ApplySection/ApplyButton/ApplyButton.mjs";
import { useJobUrl, jobUrlOriginValues } from "../CompetitiveJobs/JobCardLite/JobCardLite.mjs";
import { useTranslations } from "@vocab/react";
import { translations } from "../../../components/JobMatchAnalysis/.vocab/index.mjs";
import { JobMatchAnalysisBar } from "../JobMatchAnalysis/JobMatchAnalysisBar.mjs";
import { useAnalyticsHandlers } from "../CompanyProfile/CompanyProfile.mjs";
import { InViewTracker } from "../CompetitiveJobs/CompetitiveJobCard.mjs";
import { translations as translations$1 } from "../../../components/SkillsMatch/AlertMessages/.vocab/index.mjs";
import { translations as translations$2 } from "../../../components/SkillsMatch/SkillsMatchSignedIn/SelectableQualities/.vocab/index.mjs";
import { SkillsMatchPill } from "../JobMatchAnalysis/Skills/SkillMatchPill.mjs";
import { SkeletonLineMoving } from "../SkeletonLine/SkeltonLineMoving.mjs";
import { useAnimatedTitles, DefaultAnimationDuration } from "../JobMatchAnalysis/useAnimatedTitles.mjs";
import { StickyApply } from "../StickyApply/StickyApply.mjs";
import { squareBottomCornersOnDesktop } from "./JDVLayout.css.mjs";
const useShouldReduceDuplicateApply = () => {
  const { job: { isLinkOut, appliedDateFormatted } = {}, featureFlags } = useJobDetailsViewContext();
  return Boolean(
    featureFlags?.enableReducedDuplicatedApply && !isLinkOut && appliedDateFormatted
  );
};
const useIsApplyDisabled = () => {
  const {
    featureFlags: { hideApplyButtonOnPrivateAdvertiser } = {},
    isApplyDisabled,
    job: { isPrivateAdvertiser, isLinkOut } = {}
  } = useJobDetailsViewContext();
  const { country } = useMelwaysInfo();
  return hideApplyButtonOnPrivateAdvertiser && // if isApplyDisabled is undefined (not set), check if not PrivateAdvertiserInHK
  (isApplyDisabled ?? (isPrivateAdvertiser && country === "hk" && !isLinkOut));
};
const Disclaimer = () => {
  const { featureFlags: { enableJobMatchAnalysisPhase2 } = {} } = useJobDetailsViewContext();
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsx(Disclosure, { expandLabel: "Important Information", size: "xsmall", weight: "weak", children: enableJobMatchAnalysisPhase2 ? /* @__PURE__ */ jsx(Text, { size: "xsmall", tone: "secondary", children: t("Job match disclaimer", {
    Strong: (children) => /* @__PURE__ */ jsx(Strong, { children })
  }) }) : /* @__PURE__ */ jsx(Text, { size: "xsmall", tone: "secondary", children: "This AI analysis provides guidance by comparing your profile with this job. It should not be the sole basis for career decisions. AI results maybe inaccurate. Results are not shared with advertisers. Analysis is English-only and may be less accurate for non-English content. 5 matches per day." }) });
};
const MaxSkillsMessage = ({ t }) => /* @__PURE__ */ jsx(Text, { size: "small", tone: "critical", icon: /* @__PURE__ */ jsx(IconCritical, {}), children: t("You cant have more than 50 skills") });
const ResumeMessage = ({ t }) => /* @__PURE__ */ jsx(Text, { size: "small", tone: "info", icon: /* @__PURE__ */ jsx(IconInfo, {}), children: t("Upload a current résumé to your profile to see better matches") });
const AlertMessages = ({
  isSkillsLimitErrorShown,
  hasResume,
  track
}) => {
  const { t } = useTranslations(translations$1);
  return isSkillsLimitErrorShown || !hasResume ? /* @__PURE__ */ jsx(Box, { paddingBottom: "small", children: /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
    isSkillsLimitErrorShown && (track ? /* @__PURE__ */ jsx(InViewTracker, { trackCallback: () => track("max_skills"), children: /* @__PURE__ */ jsx(MaxSkillsMessage, { t }) }) : /* @__PURE__ */ jsx(MaxSkillsMessage, { t })),
    !hasResume && (track ? /* @__PURE__ */ jsx(InViewTracker, { trackCallback: () => track("missing_resume"), children: /* @__PURE__ */ jsx(ResumeMessage, { t }) }) : /* @__PURE__ */ jsx(ResumeMessage, { t }))
  ] }) }) : null;
};
const useAddedQualities = () => {
  const [addedQualities, setAddedQualities] = useState({
    inferred: [],
    unmatched: []
  });
  const addToAddedInferredQualities = useCallback(
    (quality) => {
      setAddedQualities((prev) => ({
        inferred: [...prev.inferred, quality],
        unmatched: prev.unmatched
      }));
    },
    [setAddedQualities]
  );
  const removeFromAddedInferredQualities = useCallback(
    (quality) => {
      setAddedQualities((prev) => ({
        inferred: prev.inferred.filter((item) => item !== quality),
        unmatched: prev.unmatched
      }));
    },
    [setAddedQualities]
  );
  const addToAddedUnmatchedQualities = useCallback(
    (quality) => {
      setAddedQualities((prev) => ({
        inferred: prev.inferred,
        unmatched: [...prev.unmatched, quality]
      }));
    },
    [setAddedQualities]
  );
  const removeFromAddedUnmatchedQualities = useCallback(
    (quality) => {
      setAddedQualities((prev) => ({
        inferred: prev.inferred,
        unmatched: prev.unmatched.filter((item) => item !== quality)
      }));
    },
    [setAddedQualities]
  );
  const updateAddedQualities = {
    inferred: {
      addToAddedQualities: addToAddedInferredQualities,
      removeFromAddedQualities: removeFromAddedInferredQualities
    },
    unmatched: {
      addToAddedQualities: addToAddedUnmatchedQualities,
      removeFromAddedQualities: removeFromAddedUnmatchedQualities
    }
  };
  return { updateAddedQualities, addedQualities };
};
const calculateTotalAndPresence = (array) => {
  const total = array?.length || 0;
  return { total, hasItems: total > 0 };
};
const determineQualitiesWhenCollapsed = (matched, inferred, unmatched) => {
  if (matched.hasItems) {
    return {
      type: "matched",
      total: matched.total
    };
  }
  if (inferred.hasItems) {
    return {
      type: "inferred",
      total: inferred.total
    };
  }
  if (unmatched.hasItems) {
    return {
      type: "unmatched",
      total: unmatched.total
    };
  }
  return {
    type: "none",
    total: 0
  };
};
const useQualitiesWhenCollapsed = () => {
  const { matched, inferred, unmatched } = useQualities();
  const _matched = calculateTotalAndPresence(matched);
  const _inferred = calculateTotalAndPresence(inferred);
  const _unmatched = calculateTotalAndPresence(unmatched);
  return determineQualitiesWhenCollapsed(_matched, _inferred, _unmatched);
};
const noop = () => {
};
const initialState = {
  isSkillsLimitErrorShown: false,
  setIsSkillsLimitErrorShown: noop,
  analyticsParams: {
    keywords: [],
    totalAddedQualities: 0,
    totalMatchedQualities: 0
  },
  updateToProfile: {},
  addedQualities: {
    inferred: [],
    unmatched: []
  },
  updateAddedQualities: {
    inferred: {
      addToAddedQualities: noop,
      removeFromAddedQualities: noop
    },
    unmatched: {
      addToAddedQualities: noop,
      removeFromAddedQualities: noop
    }
  },
  limit: 0,
  isExpanded: false,
  setIsExpanded: noop,
  qualitiesWhenCollapsed: {
    total: 0,
    type: "none"
  }
};
const SkillsMatchSignedInContext = createContext(initialState);
const useSkillsMatchSignedInContext = () => useContext(SkillsMatchSignedInContext);
const useAddedQualitiesByType = (type) => {
  const { addedQualities, updateAddedQualities } = useSkillsMatchSignedInContext();
  return {
    addedQualities: addedQualities[type],
    ...updateAddedQualities[type]
  };
};
const SkillsMatchContextProvider = ({
  children,
  onAddQuality,
  onRemoveQuality
}) => {
  const { matched } = useQualities();
  const { embedded } = useJobDetailsViewContext();
  const [isSkillsLimitErrorShown, setIsSkillsLimitErrorShown] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const { updateAddedQualities, addedQualities } = useAddedQualities();
  const totalAddedQualities = useMemo(
    () => addedQualities.inferred.length + addedQualities.unmatched.length,
    [addedQualities]
  );
  const keywords = useMemo(
    () => [...addedQualities.inferred, ...addedQualities.unmatched].map(
      (q) => q.keyword.text
    ),
    [addedQualities]
  );
  const updateToProfile = {
    add: async (quality) => {
      const { ontologyId: id, text } = quality.keyword;
      return onAddQuality?.({
        keyword: {
          ontologyId: id,
          text
        }
      });
    },
    remove: async (quality) => {
      const { ontologyId: id, text } = quality.keyword;
      return onRemoveQuality?.({
        keyword: {
          ontologyId: id,
          text
        }
      });
    }
  };
  const responsiveValue = useResponsiveValue();
  const limit = responsiveValue({
    mobile: 0,
    tablet: 3,
    desktop: embedded ? 3 : 4
  }) || 0;
  const qualitiesWhenCollapsed = useQualitiesWhenCollapsed();
  const value = {
    isSkillsLimitErrorShown,
    setIsSkillsLimitErrorShown,
    analyticsParams: {
      keywords,
      totalAddedQualities,
      totalMatchedQualities: matched.length
    },
    updateToProfile,
    addedQualities,
    updateAddedQualities,
    limit,
    isExpanded,
    setIsExpanded,
    qualitiesWhenCollapsed
  };
  return /* @__PURE__ */ jsx(SkillsMatchSignedInContext.Provider, { value, children });
};
const currentPage = "job details";
const defaultCurrentPageSection = "skills match";
const alertShownType = "notice";
const onSkillsMatchAlert = ({
  analytics,
  alertShownTone = "info",
  alertShownMessage,
  currentPageSection = defaultCurrentPageSection
}) => {
  analytics.onSkillsMatchAlert({
    currentPage,
    currentPageSection,
    alertShownTone,
    alertShownType,
    alertShownMessage
  });
};
const onSkillsMatchProfileUpdated = ({
  analytics,
  type,
  keywords,
  totalMatchedQualities,
  totalAddedQualities,
  currentPageSection = defaultCurrentPageSection
}) => {
  analytics.onSkillsMatchProfileUpdated({
    currentPage,
    currentPageSection,
    updateType: type,
    actionOrigin: "jdv - skills match",
    skillsAdded: keywords,
    skillsStartTotal: totalMatchedQualities,
    skillsFromSuggestedTotal: totalAddedQualities,
    skillsCompleteTotal: type === "add" ? totalMatchedQualities + totalAddedQualities : totalMatchedQualities - totalAddedQualities
  });
};
const useSendAnalytics = () => {
  const { t } = useTranslations(translations$2);
  const { analyticsParams } = useSkillsMatchSignedInContext();
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const sendAnalytics = useCallback(
    (actionType, params) => {
      const { totalMatchedQualities, totalAddedQualities, currentPageSection } = {
        ...analyticsParams,
        ...params
      };
      const { keywords = [] } = params ?? {};
      if (actionType === "error") {
        onSkillsMatchAlert({
          analytics,
          alertShownTone: "critical",
          alertShownMessage: t(
            "There was a problem adding this item. Try refreshing or try again later."
          )
        });
      } else {
        onSkillsMatchProfileUpdated({
          analytics,
          type: actionType,
          keywords,
          totalMatchedQualities,
          totalAddedQualities,
          ...currentPageSection ? { currentPageSection } : {}
        });
      }
    },
    [analytics, analyticsParams, t]
  );
  return sendAnalytics;
};
const useShowToast = () => {
  const { t } = useTranslations(translations$2);
  const getMessage = useCallback(
    (toastType) => {
      switch (toastType) {
        case "added":
          return t("Added to profile");
        case "removed":
          return t("Removed from profile");
        default:
          return t(
            "There was a problem adding this item. Try refreshing or try again later."
          );
      }
    },
    [t]
  );
  const _showToast = useToast();
  const showToast = useCallback(
    (toastType) => {
      _showToast({
        message: getMessage(toastType),
        tone: toastType === "error" ? "critical" : "positive",
        key: `quality-${toastType}`
      });
    },
    [_showToast, getMessage]
  );
  return showToast;
};
const useSkillsTracking = () => {
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const sendAnalytics = useCallback(
    (actionType, params) => {
      if (actionType === "error") {
        analytics.onJobMatchAnalysisAlertViewed("network_error");
      } else {
        const { updateValues = [], acceptedSuggestionsCount = 0 } = params ?? {};
        analytics.onJobMatchAnalysisProfileUpdated({
          updateType: actionType,
          updateValues,
          acceptedSuggestionsCount
        });
      }
    },
    [analytics]
  );
  return sendAnalytics;
};
const getNewSelectedQualities = (currentQualities, quality, action) => currentQualities.map((item) => ({
  ...item,
  ...item.keyword.text === quality.keyword.text ? { isSelected: action === "add" } : {}
}));
const SkillMatchPillList = ({
  matched,
  inferred,
  unmatched,
  onAddQuality,
  onRemoveQuality,
  setIsSkillsLimitErrorShown
}) => {
  const showToast = useShowToast();
  const [qualities, setQualities] = useState([
    ...inferred.map((q) => ({ ...q, isSelected: false })),
    ...unmatched.map((q) => ({ ...q, isSelected: false }))
  ]);
  const sendTracking = useSendAnalytics();
  const sendHubbleTracking = useSkillsTracking();
  const getTrackingData = (quality, selectedQualities) => ({
    currentPageSection: "job match analysis",
    keywords: [quality.keyword.text],
    totalAddedQualities: selectedQualities.filter((q) => q.isSelected).length,
    totalMatchedQualities: matched.length
  });
  const handleAddQuality = async (quality) => {
    if (onAddQuality) {
      const response = await onAddQuality(quality);
      if (response === "UpdateSkillSuccess") {
        const newSelectedQualities = getNewSelectedQualities(
          qualities,
          quality,
          "add"
        );
        const trackingData = getTrackingData(quality, newSelectedQualities);
        setQualities((_) => newSelectedQualities);
        showToast("added");
        sendTracking("add", trackingData);
        sendHubbleTracking("add", {
          acceptedSuggestionsCount: trackingData.totalAddedQualities ?? 0,
          updateValues: trackingData.keywords
        });
      } else {
        if (response === "SkillLimitReached") {
          setIsSkillsLimitErrorShown(true);
        } else {
          showToast("error");
          sendHubbleTracking("error");
        }
        sendTracking("error", getTrackingData(quality, qualities));
      }
    }
  };
  const handleRemoveQuality = async (quality) => {
    if (onRemoveQuality) {
      const response = await onRemoveQuality(quality);
      if (response !== "UpdateSkillError") {
        const newSelectedQualities = getNewSelectedQualities(
          qualities,
          quality,
          "remove"
        );
        const trackingData = getTrackingData(quality, newSelectedQualities);
        setQualities((_) => newSelectedQualities);
        showToast("removed");
        sendTracking("remove", trackingData);
        sendHubbleTracking("remove", {
          acceptedSuggestionsCount: trackingData.totalAddedQualities ?? 0,
          updateValues: trackingData.keywords
        });
      }
    }
  };
  return /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", children: [
    matched.map((quality) => /* @__PURE__ */ jsx(
      SkillsMatchPill,
      {
        quality,
        isAdded: true
      },
      quality.keyword.text
    )),
    qualities.map((quality) => /* @__PURE__ */ jsx(
      SkillsMatchPill,
      {
        quality,
        isAdded: quality.isSelected,
        onUserAction: quality.isSelected ? handleRemoveQuality : handleAddQuality
      },
      quality.keyword.text
    ))
  ] });
};
const SkillsContent = ({
  qualities,
  feedbackButtons
}) => {
  const {
    matched,
    inferred,
    unmatched,
    onAddQuality,
    onRemoveQuality,
    hasResume
  } = qualities;
  const matchedCount = matched?.length || 0;
  const totalLength = matchedCount + (inferred?.length || 0) + (unmatched?.length || 0);
  const title = `${matchedCount} of ${totalLength} skills or credentials match`;
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const [isSkillsLimitErrorShown, setIsSkillsLimitErrorShown] = useState(false);
  return /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
    /* @__PURE__ */ jsx(Text, { children: title }),
    /* @__PURE__ */ jsx(
      SkillMatchPillList,
      {
        matched: matched || [],
        inferred: inferred || [],
        unmatched: unmatched || [],
        onAddQuality,
        onRemoveQuality,
        setIsSkillsLimitErrorShown
      }
    ),
    /* @__PURE__ */ jsx(
      AlertMessages,
      {
        isSkillsLimitErrorShown,
        hasResume,
        track: analytics.onJobMatchAnalysisAlertViewed
      }
    ),
    feedbackButtons
  ] });
};
const getSkillsContent = ({
  qualities,
  feedbackButtons
}) => {
  const { shouldShowSkillsMatch } = qualities;
  return shouldShowSkillsMatch ? [
    {
      title: "Skills",
      trackingName: "skills",
      content: /* @__PURE__ */ jsx(
        SkillsContent,
        {
          qualities,
          feedbackButtons
        }
      )
    }
  ] : [];
};
const PillTabsContext = createContext(
  void 0
);
const PillTabsProvider = ({
  children,
  defaultTab = 0,
  tabTitles
}) => {
  const [activeTab, setActiveTab] = useState(defaultTab);
  return /* @__PURE__ */ jsx(
    PillTabsContext.Provider,
    {
      value: {
        activeTab,
        setActiveTab,
        tabTitles,
        activeTabTitle: tabTitles[activeTab]
      },
      children
    }
  );
};
const usePillTabs = () => {
  const context = useContext(PillTabsContext);
  if (context === void 0) {
    throw new Error("usePillTabs must be used within a PillTabsProvider");
  }
  return context;
};
const Pill = ({
  children,
  index,
  panelId,
  selected,
  setActiveTab,
  tabId,
  onChange,
  currentActiveTab
}) => {
  const handleTabChange = useCallback(() => {
    if (currentActiveTab !== index) {
      setActiveTab(index);
      onChange?.(index);
    }
  }, [currentActiveTab, index, setActiveTab, onChange]);
  const handleKeyDown = useCallback(
    (event) => {
      const { key } = event;
      const tabElements = document.querySelectorAll('[role="tab"]');
      const currentIndex = Array.from(tabElements).indexOf(event.currentTarget);
      switch (key) {
        case "ArrowLeft":
          event.preventDefault();
          const prevIndex = currentIndex === 0 ? tabElements.length - 1 : currentIndex - 1;
          tabElements[prevIndex].focus();
          setActiveTab(prevIndex);
          onChange?.(prevIndex);
          break;
        case "ArrowRight":
          event.preventDefault();
          const nextIndex = currentIndex === tabElements.length - 1 ? 0 : currentIndex + 1;
          tabElements[nextIndex].focus();
          setActiveTab(nextIndex);
          onChange?.(nextIndex);
          break;
        case "Home":
          event.preventDefault();
          tabElements[0].focus();
          setActiveTab(0);
          onChange?.(0);
          break;
        case "End":
          event.preventDefault();
          const lastIndex = tabElements.length - 1;
          tabElements[lastIndex].focus();
          setActiveTab(lastIndex);
          onChange?.(lastIndex);
          break;
        case "Enter":
        case " ":
          event.preventDefault();
          handleTabChange();
          break;
      }
    },
    [setActiveTab, onChange, handleTabChange]
  );
  return /* @__PURE__ */ jsx(
    Box,
    {
      component: "button",
      borderRadius: "full",
      boxShadow: selected ? void 0 : "borderNeutralLight",
      background: selected ? "neutral" : "surface",
      padding: "small",
      cursor: "pointer",
      onClick: handleTabChange,
      onKeyDown: handleKeyDown,
      role: "tab",
      "aria-selected": selected,
      "aria-controls": panelId,
      id: tabId,
      tabIndex: selected ? 0 : -1,
      children: /* @__PURE__ */ jsx(Text, { size: "small", children })
    }
  );
};
const PillTabs = ({
  "aria-label": ariaLabel,
  onChange
}) => {
  const { activeTab, setActiveTab, tabTitles } = usePillTabs();
  return /* @__PURE__ */ jsx(Bleed, { vertical: "small", children: /* @__PURE__ */ jsx(
    Box,
    {
      paddingBottom: "small",
      role: "tablist",
      "aria-label": ariaLabel || "Tab navigation",
      "aria-orientation": "horizontal",
      children: /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: tabTitles.map((tabTitle, index) => {
        const tabId = `pilltab-${index}`;
        const panelId = `pilltab-panel-${index}`;
        return /* @__PURE__ */ jsx(
          Pill,
          {
            selected: activeTab === index,
            index,
            setActiveTab,
            onChange,
            currentActiveTab: activeTab,
            tabId,
            panelId,
            children: tabTitle
          },
          tabTitle
        );
      }) })
    }
  ) });
};
const PillTabPanel = ({ children }) => {
  const { activeTab } = usePillTabs();
  const childrenArray = React.Children.toArray(children);
  return /* @__PURE__ */ jsx(Fragment, { children: childrenArray.map((child, index) => {
    const tabId = `pilltab-${index}`;
    const panelId = `pilltab-panel-${index}`;
    const isActive = activeTab === index;
    return /* @__PURE__ */ jsx(
      Box,
      {
        role: "tabpanel",
        id: panelId,
        "aria-labelledby": tabId,
        tabIndex: isActive ? 0 : -1,
        display: isActive ? "block" : "none",
        "aria-hidden": !isActive,
        children: child
      },
      index
    );
  }) });
};
const useJobMatchTabsTracking = () => {
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const { activeTabTitle } = usePillTabs();
  const feedbackButtonOnClick = useCallback(
    (buttonName) => {
      analytics.onJobMatchAnalysisFeedbackPressed({
        currentPageSubSection: activeTabTitle,
        buttonName
      });
    },
    [activeTabTitle, analytics]
  );
  return {
    feedbackButtonOnClick,
    editProfileOnClick: analytics.onJobMatchAnalysisEditProfilePressed
  };
};
const FeedbackThumb = ({
  direction,
  id,
  label,
  activeThumb,
  setActiveThumb
}) => {
  const { feedbackButtonOnClick } = useJobMatchTabsTracking();
  const onClick = useCallback(() => {
    setActiveThumb((prev) => prev === direction ? null : direction);
    feedbackButtonOnClick(direction === "up" ? "like" : "dislike");
  }, [direction, feedbackButtonOnClick, setActiveThumb]);
  return /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      bleed: true,
      variant: activeThumb === direction ? "soft" : "transparent",
      size: "standard",
      icon: /* @__PURE__ */ jsx(IconThumb, { direction }),
      label,
      id,
      onClick
    }
  );
};
const FeedbackButtons = ({
  activeFeedbackThumb,
  setActiveFeedbackThumb
}) => /* @__PURE__ */ jsx(Inline, { space: "xsmall", alignY: "center", children: activeFeedbackThumb !== null ? /* @__PURE__ */ jsx(Text, { size: "small", children: "Thank you for your feedback" }) : /* @__PURE__ */ jsx(Fragment, { children: /* @__PURE__ */ jsxs(
  Box,
  {
    flexDirection: "row",
    display: "flex",
    justifyContent: "center",
    gap: "medium",
    children: [
      /* @__PURE__ */ jsx(
        FeedbackThumb,
        {
          id: "thumbs-up",
          direction: "up",
          label: "Helpful",
          activeThumb: activeFeedbackThumb,
          setActiveThumb: setActiveFeedbackThumb
        }
      ),
      /* @__PURE__ */ jsx(
        FeedbackThumb,
        {
          id: "thumbs-down",
          direction: "down",
          label: "Not helpful",
          activeThumb: activeFeedbackThumb,
          setActiveThumb: setActiveFeedbackThumb
        }
      )
    ]
  }
) }) });
const DefaultMessage = ({
  message,
  tone
}) => /* @__PURE__ */ jsx(Stack, { space: "small", children: /* @__PURE__ */ jsx(Notice, { tone, children: /* @__PURE__ */ jsx(Text, { children: message }) }) });
const getCommonContent = ({
  message,
  tone
}) => {
  const yourMatch = {
    title: "Your match",
    trackingName: "your_match",
    content: /* @__PURE__ */ jsx(DefaultMessage, { message, tone })
  };
  const nextSteps = {
    title: "Next steps",
    trackingName: "next_steps",
    content: /* @__PURE__ */ jsx(DefaultMessage, { message, tone })
  };
  return [yourMatch, nextSteps];
};
const CopyButton = ({
  label,
  textToCopy
}) => {
  const { feedbackButtonOnClick } = useJobMatchTabsTracking();
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    feedbackButtonOnClick("copy");
    if (textToCopy && navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(textToCopy);
        setCopied(true);
        setTimeout(() => setCopied(false), 2e3);
      } catch (error) {
        throw error;
      }
    }
  };
  return /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      icon: /* @__PURE__ */ jsx(IconCopy, {}),
      label: copied ? "Copied" : label,
      bleed: true,
      variant: "transparent",
      size: "standard",
      onClick: handleCopy
    }
  );
};
const defaultShouldApplyMessage = `You're a strong match for this role. Consider applying.`;
const ShouldApplyMessage = ({
  shouldApplyMessage
}) => /* @__PURE__ */ jsx(Box, { paddingY: "xxsmall", children: /* @__PURE__ */ jsx(Text, { tone: "positive", icon: /* @__PURE__ */ jsx(IconPositive, {}), children: shouldApplyMessage ?? defaultShouldApplyMessage }) });
const FeedbackRow = ({
  textToCopy,
  isDoneFeedback,
  feedbackButtons,
  label,
  allocationMessage
}) => {
  const { featureFlags: { enableJobMatchAnalysisPhase2 } = {} } = useJobDetailsViewContext();
  return enableJobMatchAnalysisPhase2 ? /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Spread, { space: "small", children: [
      /* @__PURE__ */ jsxs(Inline, { space: "medium", alignY: "center", children: [
        /* @__PURE__ */ jsx(CopyButton, { label, textToCopy }),
        !isDoneFeedback && feedbackButtons
      ] }),
      /* @__PURE__ */ jsx(Text, { children: allocationMessage })
    ] }),
    isDoneFeedback && feedbackButtons
  ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs(Inline, { space: "medium", alignY: "center", children: [
      /* @__PURE__ */ jsx(CopyButton, { label, textToCopy }),
      !isDoneFeedback && feedbackButtons
    ] }),
    isDoneFeedback && feedbackButtons
  ] });
};
const getCompletedContent = ({
  feedbackButtons,
  isDoneFeedback,
  matchResults
}) => {
  if (!matchResults) {
    return [];
  }
  const {
    summary,
    recommendations,
    shouldApply,
    shouldApplyMessage,
    allocationMessage
  } = matchResults;
  const yourMatchContent = {
    title: "Your match",
    trackingName: "your_match",
    content: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsx(List, { children: summary.split("\n").map((line) => /* @__PURE__ */ jsx(Text, { children: line }, line)) }),
      shouldApply && /* @__PURE__ */ jsx(ShouldApplyMessage, { shouldApplyMessage }),
      /* @__PURE__ */ jsx(
        FeedbackRow,
        {
          feedbackButtons,
          isDoneFeedback,
          textToCopy: summary,
          allocationMessage,
          label: "Copy Your match"
        }
      )
    ] })
  };
  const nextStepsContent = {
    title: "Next steps",
    trackingName: "next_steps",
    content: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
      /* @__PURE__ */ jsx(List, { type: "number", children: recommendations.split("\n").map((line) => /* @__PURE__ */ jsx(Text, { children: line.replace(/^\d+\.\s*/, "") }, line)) }),
      shouldApply && /* @__PURE__ */ jsx(ShouldApplyMessage, { shouldApplyMessage }),
      /* @__PURE__ */ jsx(
        FeedbackRow,
        {
          feedbackButtons,
          isDoneFeedback,
          textToCopy: recommendations,
          label: "Copy Next steps",
          allocationMessage
        }
      )
    ] })
  };
  return [yourMatchContent, nextStepsContent];
};
const ProfileNotEligible = ({
  message,
  qualifiers
}) => {
  const { editProfileOnClick } = useJobMatchTabsTracking();
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(Notice, { tone: "info", children: /* @__PURE__ */ jsx(Text, { children: message }) }),
    /* @__PURE__ */ jsx(Box, { padding: "small", children: /* @__PURE__ */ jsx(List, { tone: "info", children: qualifiers?.map((qualifier) => /* @__PURE__ */ jsx(Text, { tone: "info", children: qualifier }, qualifier)) }) }),
    /* @__PURE__ */ jsx(Box, { paddingTop: "medium", children: /* @__PURE__ */ jsx(Inline, { space: "small", children: /* @__PURE__ */ jsx(
      ButtonLink,
      {
        size: "small",
        tone: "formAccent",
        variant: "ghost",
        href: "/profile/me",
        target: "_blank",
        onClick: editProfileOnClick,
        children: "Edit Profile"
      }
    ) }) })
  ] });
};
const getProfileNotEligibleContent = ({
  message,
  qualifiers
}) => {
  const yourMatch = {
    title: "Your match",
    trackingName: "your_match",
    content: /* @__PURE__ */ jsx(ProfileNotEligible, { message, qualifiers })
  };
  const nextSteps = {
    title: "Next steps",
    trackingName: "next_steps",
    content: /* @__PURE__ */ jsx(ProfileNotEligible, { message, qualifiers })
  };
  return [yourMatch, nextSteps];
};
const JobMatchContent = ({
  matchResults,
  status,
  errorMessage,
  qualities,
  qualifiers
}) => {
  const message = errorMessage || `Something's gone wrong; please try again later`;
  const { onAnalyticsEvent } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const analyticsTracking = (index) => {
    if (matchContents[index]) {
      const { title, trackingName } = matchContents[index];
      analytics.onJobMatchAnalysisPillTabChanged({ title, trackingName });
    }
  };
  const [activeFeedbackThumb, setActiveFeedbackThumb] = useState(null);
  const [matchContents, setMatchContents] = useState([]);
  useEffect(() => {
    const buildMatchContents = () => {
      let baseContents = [];
      switch (status) {
        case "Completed":
          baseContents = getCompletedContent({
            matchResults,
            isDoneFeedback: activeFeedbackThumb !== null,
            feedbackButtons: /* @__PURE__ */ jsx(
              FeedbackButtons,
              {
                activeFeedbackThumb,
                setActiveFeedbackThumb
              }
            )
          });
          break;
        case "ProfileNotEligible":
          baseContents = getProfileNotEligibleContent({ message, qualifiers });
          break;
        case "InProgress":
        case "AllocationExhausted":
          baseContents = getCommonContent({
            message,
            tone: "info"
          });
          break;
        case "Error":
        case "NotExists":
        default:
          baseContents = getCommonContent({
            message,
            tone: "critical"
          });
          break;
      }
      const skillsContent = qualities ? getSkillsContent({
        qualities,
        feedbackButtons: /* @__PURE__ */ jsx(
          FeedbackButtons,
          {
            activeFeedbackThumb,
            setActiveFeedbackThumb
          }
        )
      }) : [];
      return [...baseContents, ...skillsContent];
    };
    setMatchContents(buildMatchContents());
  }, [
    activeFeedbackThumb,
    message,
    matchResults,
    status,
    qualities,
    qualifiers
  ]);
  const pillTabTitles = matchContents.map((item) => item.title);
  const pillTabContents = matchContents.map((item) => item.content);
  return /* @__PURE__ */ jsxs(PillTabsProvider, { tabTitles: pillTabTitles, children: [
    /* @__PURE__ */ jsx(
      PillTabs,
      {
        "aria-label": "Job Match Analysis Tabs",
        onChange: analyticsTracking
      }
    ),
    /* @__PURE__ */ jsx(PillTabPanel, { children: pillTabContents.map((content, index) => /* @__PURE__ */ jsx(Box, { padding: "none", role: "tabpanel", children: content }, index)) })
  ] });
};
const JobMatchContentAccordion = ({
  children,
  header,
  onClick,
  showContent
}) => /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
  /* @__PURE__ */ jsx(
    Box,
    {
      component: "button",
      type: "button",
      cursor: "pointer",
      width: "full",
      textAlign: "left",
      padding: "none",
      children: /* @__PURE__ */ jsxs(
        Box,
        {
          display: "flex",
          alignItems: "center",
          flexDirection: "row",
          width: "full",
          gap: "small",
          children: [
            header,
            /* @__PURE__ */ jsx(Box, { padding: "none", onClick, children: /* @__PURE__ */ jsx(IconChevron, { direction: showContent ? "up" : "down" }) })
          ]
        }
      )
    }
  ),
  /* @__PURE__ */ jsx(Box, { padding: "none", display: showContent ? "block" : "none", children })
] });
const SkeletonBullet = () => /* @__PURE__ */ jsx(Box, { style: { marginTop: 6 }, children: /* @__PURE__ */ jsx(
  SkeletonLineMoving,
  {
    type: "button",
    width: SkeletonLineHeight,
    height: SkeletonLineHeight
  }
) });
const SkeletonLineHeight = 16;
const ButtonRadius = 24;
const JobMatchSkeleton = () => /* @__PURE__ */ jsx(Bleed, { top: "small", children: /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
  /* @__PURE__ */ jsxs(Inline, { space: "xxsmall", children: [
    /* @__PURE__ */ jsx(SkeletonLineMoving, { type: "text", width: "auto", size: "small", children: /* @__PURE__ */ jsx(Box, { padding: "small", style: { opacity: 0, visibility: "hidden" }, children: "Your match" }) }),
    /* @__PURE__ */ jsx(SkeletonLineMoving, { type: "text", width: "auto", size: "small", children: /* @__PURE__ */ jsx(Box, { padding: "small", style: { opacity: 0, visibility: "hidden" }, children: "Next steps" }) }),
    /* @__PURE__ */ jsx(SkeletonLineMoving, { type: "text", width: "auto", size: "small", children: /* @__PURE__ */ jsx(Box, { padding: "small", style: { opacity: 0, visibility: "hidden" }, children: "Skills" }) })
  ] }),
  /* @__PURE__ */ jsxs(List, { type: "icon", icon: /* @__PURE__ */ jsx(SkeletonBullet, {}), children: [
    /* @__PURE__ */ jsxs(Inline, { space: "small", children: [
      /* @__PURE__ */ jsx(
        SkeletonLineMoving,
        {
          type: "text",
          width: "100%",
          height: SkeletonLineHeight,
          size: "large"
        }
      ),
      /* @__PURE__ */ jsx(
        SkeletonLineMoving,
        {
          type: "text",
          width: "80%",
          height: SkeletonLineHeight,
          size: "large"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(Inline, { space: "small", children: [
      /* @__PURE__ */ jsx(
        SkeletonLineMoving,
        {
          type: "text",
          width: "100%",
          height: SkeletonLineHeight,
          size: "large"
        }
      ),
      /* @__PURE__ */ jsx(
        SkeletonLineMoving,
        {
          type: "text",
          width: "80%",
          height: SkeletonLineHeight,
          size: "large"
        }
      )
    ] }),
    /* @__PURE__ */ jsxs(Inline, { space: "small", children: [
      /* @__PURE__ */ jsx(
        SkeletonLineMoving,
        {
          type: "text",
          width: "100%",
          height: SkeletonLineHeight,
          size: "large"
        }
      ),
      /* @__PURE__ */ jsx(
        SkeletonLineMoving,
        {
          type: "text",
          width: "80%",
          height: SkeletonLineHeight,
          size: "large"
        }
      )
    ] })
  ] }),
  /* @__PURE__ */ jsxs(Inline, { space: "small", children: [
    /* @__PURE__ */ jsx(
      SkeletonLineMoving,
      {
        type: "button",
        width: ButtonRadius,
        height: ButtonRadius
      }
    ),
    /* @__PURE__ */ jsx(
      SkeletonLineMoving,
      {
        type: "button",
        width: ButtonRadius,
        height: ButtonRadius
      }
    ),
    /* @__PURE__ */ jsx(
      SkeletonLineMoving,
      {
        type: "button",
        width: ButtonRadius,
        height: ButtonRadius
      }
    )
  ] })
] }) });
const errorMap = {
  AllocationExhausted: "limit_reached",
  Error: "network_error",
  ProfileNotEligible: "outdated_profile"
};
const useJobMatchTracking = (showContent, { trackingId, status, matchResult }) => {
  const {
    onAnalyticsEvent,
    job: { id: jobId }
  } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const usePreviousStatus = (value) => {
    const ref = useRef(value);
    useEffect(() => {
      ref.current = value;
    });
    return ref.current;
  };
  const previousStatus = usePreviousStatus(status);
  useEffect(() => {
    if (previousStatus !== status) {
      if (status === "Completed") {
        analytics.onJobMatchAnalysisResultViewed({
          serviceToken: trackingId,
          alreadyExists: matchResult?.isExistingMatch === true,
          limitReached: false,
          isStrongMatch: matchResult?.shouldApply === true
        });
      } else if (errorMap[status]) {
        analytics.onJobMatchAnalysisAlertViewed(errorMap[status]);
      }
    }
  }, [
    status,
    analytics,
    trackingId,
    previousStatus,
    matchResult?.isExistingMatch,
    matchResult?.shouldApply
  ]);
  const { ref: trackedComponent } = useInView({
    onChange(inView) {
      if (inView) {
        analytics.onJobMatchAnalysisViewed();
        if (status === "Completed") {
          analytics.onJobMatchAnalysisResultViewed({
            limitReached: false,
            alreadyExists: matchResult?.isExistingMatch === true,
            serviceToken: trackingId,
            isStrongMatch: matchResult?.shouldApply === true
          });
        } else if (status === "Error") {
          analytics.onJobMatchAnalysisAlertViewed("network_error");
        }
      }
    },
    threshold: 0.5,
    triggerOnce: true
  });
  const trackPillTabChanged = ({
    title,
    trackingName
  }) => {
    analytics.onJobMatchAnalysisPillTabChanged({ title, trackingName });
  };
  const trackJobMatchAnalysisPressed = () => analytics.onJobMatchAnalysisPressed({
    action: !showContent ? "EXPAND" : "COLLAPSE",
    serviceToken: trackingId,
    jobId: jobId ?? ""
  });
  return {
    trackedComponent,
    trackJobMatchAnalysisPressed,
    trackPillTabChanged
  };
};
const JobMatchAnalysisSignedIn = ({
  jobMatchAnalysis,
  qualities
}) => {
  const {
    matchResult,
    status,
    onCreateMatch,
    errorMessage,
    qualifiers,
    waitForMs
  } = jobMatchAnalysis;
  const { title, loadAnimatedTitles, isLoadingAnimation, clearAnimation } = useAnimatedTitles(
    waitForMs && waitForMs > 0 ? waitForMs : DefaultAnimationDuration
  );
  const [isEligible, setIsEligible] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const { trackedComponent, trackJobMatchAnalysisPressed } = useJobMatchTracking(showContent, jobMatchAnalysis);
  useEffect(() => {
    setIsEligible(status === "NotExists");
    setShowContent(status !== "NotExists");
  }, [status]);
  useEffect(() => {
    if (status !== "InProgress" && isLoadingAnimation) {
      clearAnimation();
    }
    if (status === "InProgress" && !isLoadingAnimation) {
      loadAnimatedTitles();
    }
  }, [status, loadAnimatedTitles, isLoadingAnimation, clearAnimation]);
  const onClick = useCallback(() => {
    if (status === "InProgress") {
      return;
    }
    trackJobMatchAnalysisPressed();
    if (isEligible) {
      onCreateMatch();
      setIsEligible(false);
    } else {
      setShowContent(!showContent);
    }
  }, [
    status,
    isEligible,
    onCreateMatch,
    showContent,
    trackJobMatchAnalysisPressed
  ]);
  const clickable = useMemo(
    () => showContent ? {
      cursor: "default",
      onClick: void 0
    } : {
      cursor: "pointer",
      onClick
    },
    [showContent, onClick]
  );
  return /* @__PURE__ */ jsx(
    Box,
    {
      width: "full",
      borderRadius: "large",
      boxShadow: "borderNeutralLight",
      padding: "medium",
      ref: trackedComponent,
      ...clickable,
      children: /* @__PURE__ */ jsx(
        JobMatchContentAccordion,
        {
          header: /* @__PURE__ */ jsx(
            JobMatchAnalysisBar,
            {
              onClick,
              title,
              isLoadingAnimation
            }
          ),
          showContent,
          onClick,
          children: /* @__PURE__ */ jsx(Stack, { space: "medium", children: status === "InProgress" ? /* @__PURE__ */ jsx(JobMatchSkeleton, {}) : /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              JobMatchContent,
              {
                matchResults: matchResult,
                status,
                errorMessage,
                qualities,
                qualifiers
              }
            ),
            /* @__PURE__ */ jsx(Disclaimer, {})
          ] }) })
        }
      )
    }
  );
};
const ArrowHitsTargetSvg = ({ size }) => /* @__PURE__ */ jsxs(
  "svg",
  {
    xmlns: "http://www.w3.org/2000/svg",
    fill: "none",
    viewBox: "0 0 128 128",
    width: size,
    height: size,
    children: [
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#4166A4",
          d: "M92.057 95.909a41.122 41.122 0 0 1-10.028 5.112l7.646 13.242a3.474 3.474 0 0 0 4.745 1.271l3.718-2.147a3.473 3.473 0 0 0 1.271-4.744l-7.352-12.734ZM37.713 108.643a3.473 3.473 0 0 0 1.271 4.744l3.719 2.147a3.474 3.474 0 0 0 4.744-1.271l7.646-13.242a41.12 41.12 0 0 1-10.028-5.112l-7.352 12.734Z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#EE399E",
          d: "M109.764 62.09c0-22.72-18.483-41.203-41.203-41.203-22.72 0-41.203 18.483-41.203 41.203 0 13.99 7.016 26.368 17.707 33.819a41.122 41.122 0 0 0 10.028 5.112 41.014 41.014 0 0 0 13.468 2.272c4.716 0 9.245-.807 13.468-2.272a41.122 41.122 0 0 0 10.028-5.112c10.692-7.452 17.707-19.828 17.707-33.82ZM61.953 87.698C52.669 85.3 45.345 77.975 42.95 68.69a26.392 26.392 0 0 1-.834-6.601c0-.911.047-1.812.137-2.7C43.61 46.072 54.89 35.645 68.56 35.645c2.48 0 4.879.351 7.157.993.651.183 1.296.384 1.926.615.81.297 1.601.632 2.372 1.004 8.09 3.904 13.903 11.81 14.853 21.133a26.615 26.615 0 0 1-.698 9.3c-1.68 6.52-5.796 12.066-11.309 15.626a26.278 26.278 0 0 1-14.3 4.218 26.39 26.39 0 0 1-6.61-.836Z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#EE399E",
          d: "M80.246 62.09c0-6.454-5.232-11.686-11.685-11.686-1.003 0-1.97.14-2.9.378-.465.119-.92.262-1.361.435-.45.176-.89.37-1.31.597a11.684 11.684 0 0 0-6.115 10.276c0 2.277.662 4.395 1.788 6.192a11.787 11.787 0 0 0 1.634 2.07 11.649 11.649 0 0 0 8.264 3.423c6.453 0 11.685-5.231 11.685-11.685Z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#fff",
          d: "M94.869 59.39c-.95-9.323-6.763-17.229-14.853-21.133a26.229 26.229 0 0 0-2.371-1.004 26.052 26.052 0 0 0-1.927-.615 26.359 26.359 0 0 0-7.157-.992c-13.67 0-24.95 10.426-26.307 23.744a26.618 26.618 0 0 0 .697 9.3c2.395 9.285 9.718 16.61 19.002 19.008 2.113.546 4.327.836 6.608.836a26.28 26.28 0 0 0 14.301-4.218c5.513-3.56 9.629-9.106 11.31-15.625a26.618 26.618 0 0 0 .697-9.3ZM68.56 73.775a11.648 11.648 0 0 1-8.263-3.422 11.778 11.778 0 0 1-1.634-2.07 11.614 11.614 0 0 1-1.788-6.193c0-4.437 2.473-8.297 6.115-10.276.42-.227.86-.42 1.31-.597.44-.173.896-.316 1.361-.435a11.66 11.66 0 0 1 2.9-.378c6.453 0 11.685 5.232 11.685 11.686s-5.232 11.685-11.686 11.685Z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#FDC221",
          d: "M20.356 26.096c-2.055.392-2.833 2.922-1.354 4.401l9.597 9.597c.61.61 1.482.879 2.33.717l8.985-1.713c.79-.15 1.385-.62 1.742-1.221L28.351 24.572l-7.995 1.524ZM45.334 33.677l1.713-8.985a2.605 2.605 0 0 0-.717-2.33l-9.597-9.597c-1.479-1.478-4.01-.7-4.401 1.354l-1.524 7.995L44.113 35.42a2.556 2.556 0 0 0 1.22-1.743Z"
        }
      ),
      /* @__PURE__ */ jsx(
        "path",
        {
          fill: "#072462",
          d: "m70.103 61.41-25.99-25.99-13.305-13.306-2.18-2.18a1.739 1.739 0 0 0-2.457 0 1.739 1.739 0 0 0 0 2.458l2.18 2.18 13.305 13.305 25.99 25.99c.34.338.784.507 1.228.507a1.739 1.739 0 0 0 1.228-2.965Z"
        }
      )
    ]
  }
);
const JobMatchAnalysisSignedOutNudge = ({
  showNudge,
  onClose
}) => {
  const { onAnalyticsEvent, signInUrl, registerUrl } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  return /* @__PURE__ */ jsx(
    Dialog,
    {
      title: "",
      open: showNudge,
      onClose,
      description: /* @__PURE__ */ jsxs(Spread, { space: "medium", alignY: "top", children: [
        /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          /* @__PURE__ */ jsx(Heading, { level: "4", children: "Sign in or register to see:" }),
          /* @__PURE__ */ jsxs(List, { children: [
            /* @__PURE__ */ jsx(Text, { children: "Your experience match" }),
            /* @__PURE__ */ jsx(Text, { children: "Your growth areas" }),
            /* @__PURE__ */ jsx(Text, { children: "Your matching skills" })
          ] }),
          /* @__PURE__ */ jsxs(Inline, { space: "small", children: [
            /* @__PURE__ */ jsx(
              ButtonLink,
              {
                variant: "solid",
                tone: "formAccent",
                href: signInUrl,
                onClick: analytics.onJobMatchSignInPressed,
                children: "Sign In"
              }
            ),
            /* @__PURE__ */ jsx(
              ButtonLink,
              {
                variant: "transparent",
                href: registerUrl,
                onClick: analytics.onJobMatchRegisterPressed,
                children: "Register"
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: /* @__PURE__ */ jsx(ArrowHitsTargetSvg, { size: 152 }) })
      ] }),
      children: ""
    }
  );
};
const JobMatchAnalysisSignedOut = () => {
  const [showNudge, setShowNudge] = useState(false);
  const { title } = useAnimatedTitles();
  const {
    onAnalyticsEvent,
    jobMatchAnalysis: { trackingId } = {},
    job: { id: jobId }
  } = useJobDetailsViewContext();
  const analytics = useAnalyticsHandlers(onAnalyticsEvent);
  const onBarClick = useCallback(() => {
    setShowNudge(true);
    analytics.onJobMatchAnalysisPressed({
      action: "EXPAND",
      serviceToken: trackingId,
      jobId: jobId ?? ""
    });
  }, [analytics, jobId, trackingId]);
  const { ref: trackedComponent } = useInView({
    onChange(inView) {
      if (inView) {
        analytics.onJobMatchAnalysisViewed();
      }
    },
    threshold: 0.5,
    triggerOnce: true
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Box,
      {
        width: "full",
        borderRadius: "large",
        boxShadow: "borderNeutralLight",
        padding: "medium",
        cursor: "pointer",
        onClick: onBarClick,
        ref: trackedComponent,
        children: /* @__PURE__ */ jsx(
          JobMatchAnalysisBar,
          {
            onClick: onBarClick,
            title,
            isLoadingAnimation: false
          }
        )
      }
    ),
    /* @__PURE__ */ jsx(
      JobMatchAnalysisSignedOutNudge,
      {
        showNudge,
        onClose: () => setShowNudge(false)
      }
    )
  ] });
};
const JobMatchAnalysis = () => {
  const { isLoggedIn, jobMatchAnalysis, qualities } = useJobDetailsViewContext();
  if (!isLoggedIn) {
    return /* @__PURE__ */ jsx(JobMatchAnalysisSignedOut, {});
  }
  return jobMatchAnalysis ? /* @__PURE__ */ jsx(
    JobMatchAnalysisSignedIn,
    {
      jobMatchAnalysis,
      qualities
    }
  ) : null;
};
const MainContentCTAsWrapperView = ({
  actions
}) => {
  const content = /* @__PURE__ */ jsx(Inline, { space: "small", children: actions });
  return content;
};
const MainContentCTAsWrapper$1 = ({
  layoutOverride,
  actions,
  userHasAlreadyApplied
}) => {
  const content = /* @__PURE__ */ jsx(MainContentCTAsWrapperView, { actions });
  if (userHasAlreadyApplied) {
    return content;
  }
  switch (layoutOverride) {
    case "desktop":
      return content;
    case "mobile":
      return null;
    default:
      return /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: content });
  }
};
const MainContentCTAsWrapper = forwardRef(
  (props, ref) => /* @__PURE__ */ jsx(Box, { ref, children: /* @__PURE__ */ jsx(MainContentCTAsWrapper$1, { ...props }) })
);
const HKPrivateAdvertiserNotice = ({
  phoneNumber
}) => {
  const hideApplyButtonForPrivateAds = useIsApplyDisabled();
  if (!hideApplyButtonForPrivateAds) {
    return null;
  }
  return /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsx(Notice, { tone: "info", children: phoneNumber ? /* @__PURE__ */ jsxs(Text, { children: [
    "Interested in this role? Call ",
    phoneNumber,
    " to find out more."
  ] }) : /* @__PURE__ */ jsx(Text, { children: "This role is advertised privately with no contact details. To protect your privacy, candidates in Hong Kong can't apply." }) }) });
};
const defaultBoxPaddingX = {
  mobile: "xsmall",
  tablet: "gutter",
  desktop: "xlarge"
};
const boxPaddingX = (layoutOverride) => {
  switch (layoutOverride) {
    case "desktop":
      return "xlarge";
    case "mobile":
      return "xsmall";
    default:
      return defaultBoxPaddingX;
  }
};
const getPaddingX = ({
  layoutOverride,
  embedded
}) => {
  if (embedded) {
    return void 0;
  }
  return boxPaddingX(layoutOverride);
};
const MAIN_CONTENT_PADDING_Y = "xxlarge";
const JDVLayout = (props) => {
  const {
    id,
    jobUrlOpts,
    renderRelativePaths,
    jobUrl,
    jobTitle,
    companyName,
    applyAction,
    saveAction,
    branding,
    editProps,
    badgeAndDisplayTags,
    header,
    headerAdditional,
    mainContent,
    menu,
    showCoverThumbnail,
    layoutOverride,
    embedded,
    restrictedApplicationNotice,
    stickApplyButtonOnDesktop = false,
    onAnalyticsEvent,
    hideOpenInNewTab,
    hideJobLink,
    phoneNumber,
    isExpired,
    enableJobMatchAnalysis
  } = props;
  const showCover = editProps?.isEditMode || Boolean(branding?.assets?.cover);
  const showLogo = editProps?.isEditMode || Boolean(branding?.assets?.logo);
  const actionsRef = useRef();
  const [isBelowActions, setIsBelowActions] = useState(false);
  const { ref: actionsInViewRef } = useInView({
    onChange(inView) {
      const actionsRect = actionsRef?.current?.getBoundingClientRect() ?? {
        top: 0,
        bottom: 0
      };
      const isScrollingBelowActions = actionsRect.top >= 0 && actionsRect.bottom <= (window?.innerHeight || document?.documentElement.clientHeight) || actionsRect.bottom < 0 || actionsRect.top < 0;
      if (inView || !isScrollingBelowActions) {
        setIsBelowActions(false);
      } else {
        setIsBelowActions(true);
      }
    }
  });
  const setActionsRef = useCallback(
    (node) => {
      actionsRef.current = node;
      actionsInViewRef(node);
    },
    [actionsInViewRef]
  );
  const userHasAlreadyApplied = useShouldReduceDuplicateApply();
  const hideApplyButton = useIsApplyDisabled();
  const actions = /* @__PURE__ */ jsxs(Columns, { space: { mobile: "xxsmall", tablet: "small" }, children: [
    applyAction && !hideApplyButton ? /* @__PURE__ */ jsx(Column, { children: applyAction }) : null,
    saveAction && !hideApplyButton ? /* @__PURE__ */ jsx(Column, { width: "content", children: saveAction }) : null,
    saveAction && hideApplyButton ? /* @__PURE__ */ jsx(Column, { children: saveAction }) : null
  ] });
  const paddingX = getPaddingX({ layoutOverride, embedded });
  const showStickyApplyToolbar = stickApplyButtonOnDesktop && isBelowActions;
  const stickyJobUrl = useJobUrl({
    id,
    jobUrlOpts,
    overrideValues: {
      origin: jobUrlOriginValues.stickyTitle
    },
    renderRelativePaths,
    isExpired
  });
  return /* @__PURE__ */ jsxs(
    Box,
    {
      background: !embedded ? "surface" : void 0,
      paddingTop: !embedded && !showCover ? "large" : void 0,
      paddingBottom: !embedded ? "large" : void 0,
      children: [
        /* @__PURE__ */ jsx(
          StickyApply,
          {
            jobTitle,
            jobUrl: hideJobLink || jobUrlOpts?.hide ? void 0 : jobUrl || stickyJobUrl,
            companyName,
            layoutOverride,
            actions,
            stickApplyButtonOnDesktop: showStickyApplyToolbar,
            applyAction,
            saveAction,
            onAnalyticsEvent,
            paddingX,
            embedded,
            hideOpenInNewTab
          }
        ),
        /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
          showCover ? /* @__PURE__ */ jsx(
            Box,
            {
              overflow: "hidden",
              borderRadius: { desktop: embedded ? "large" : "xlarge" },
              className: squareBottomCornersOnDesktop,
              children: /* @__PURE__ */ jsx(
                CoverImage,
                {
                  cover: branding?.assets?.cover,
                  isEditMode: editProps?.isEditMode,
                  onAddCoverClick: editProps?.onAddCoverClick,
                  onRemoveCoverClick: editProps?.onRemoveCoverClick,
                  onReplaceCoverClick: editProps?.onReplaceCoverClick,
                  onRepositionCoverClick: editProps?.onRepositionCoverClick,
                  showThumbnail: showCoverThumbnail,
                  showLoading: editProps?.showCoverLoading
                }
              )
            }
          ) : null,
          /* @__PURE__ */ jsx(Box, { paddingX, children: /* @__PURE__ */ jsxs(Stack, { space: "xlarge", children: [
            /* @__PURE__ */ jsxs(Stack, { space: "large", children: [
              /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                !showLogo ? badgeAndDisplayTags : null,
                /* @__PURE__ */ jsxs(Columns, { space: "small", children: [
                  /* @__PURE__ */ jsx(Column, { children: showLogo ? /* @__PURE__ */ jsx(
                    BrandLogo,
                    {
                      logo: branding?.assets?.logo,
                      isEditMode: editProps?.isEditMode,
                      withPlaceholder: editProps?.isEditMode,
                      onAddLogoClick: editProps?.onAddLogoClick,
                      onReplaceLogoClick: editProps?.onReplaceLogoClick,
                      showLoading: editProps?.showLogoLoading
                    }
                  ) : header }),
                  /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(Inline, { space: "none", children: /* @__PURE__ */ jsx(Bleed, { top: !showLogo ? "xxsmall" : void 0, children: menu }) }) })
                ] })
              ] }),
              showLogo ? /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                badgeAndDisplayTags,
                header
              ] }) : null,
              headerAdditional,
              /* @__PURE__ */ jsx(Hidden, { below: "tablet", children: Boolean(actions) && /* @__PURE__ */ jsx(
                MainContentCTAsWrapper,
                {
                  ref: setActionsRef,
                  layoutOverride,
                  actions,
                  userHasAlreadyApplied
                }
              ) }),
              /* @__PURE__ */ jsx(HKPrivateAdvertiserNotice, { phoneNumber }),
              enableJobMatchAnalysis && /* @__PURE__ */ jsx(JobMatchAnalysis, {})
            ] }),
            restrictedApplicationNotice && /* @__PURE__ */ jsx(Stack, { space: "large", children: restrictedApplicationNotice }),
            /* @__PURE__ */ jsx(Stack, { space: MAIN_CONTENT_PADDING_Y, children: mainContent })
          ] }) })
        ] })
      ]
    }
  );
};
export {
  AlertMessages,
  JDVLayout,
  SkillsMatchContextProvider,
  defaultBoxPaddingX,
  onSkillsMatchAlert,
  useAddedQualitiesByType,
  useIsApplyDisabled,
  useSendAnalytics,
  useShouldReduceDuplicateApply,
  useShowToast,
  useSkillsMatchSignedInContext
};
